import {
  Banner,
  Button,
  Buttons,
  Container,
  ControlGroup,
  Editable,
  EditableChangedEventArgs,
  Footer,
  Form,
  Group,
  Groups,
  Header,
  IControlChangeEventArgs,
  Input,
  Layout,
  Level,
  LevelGroup,
  LevelItem,
  MainContent,
  Section,
  Select,
  Text,
} from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  useAccountDetails,
  useAccountUsers,
  useUpdateAccountMutation,
} from '@/features/accounts/accountApi'
import { EntityConditioner } from '@/utils/entity-conditioner'
import { isValidEmail } from '@/utils/validation'

export type IAccountDetailsComponentProps = {}

type EditableAccountFields = {
  display_name: string
  contact_email: string
  is_active: boolean
}

const AccountDetailsView = (_props: IAccountDetailsComponentProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: idParam } = useParams<{ id: string }>()
  const accountUID =
    idParam && !isNaN(parseInt(idParam, 10)) ? parseInt(idParam, 10) : undefined

  const {
    data: accountData,
    isLoading,
    isError,
  } = useAccountDetails(accountUID)

  const { data: accountUsersData, isError: isAccountUsersError } =
    useAccountUsers(accountUID)

  const [updateAccount, { isLoading: isSaving }] = useUpdateAccountMutation()

  const [editedFields, setEditedFields] = React.useState<EditableAccountFields>(
    {
      display_name: '',
      contact_email: '',
      is_active: true,
    },
  )

  React.useEffect(() => {
    if (isAccountUsersError) {
      // i18n key kept as-is: the copy says "Members" and the account-members-* components keep that name (KPS-555).
      toast.error(t('ERROR_LOADING_ACCOUNT_MEMBERSHIPS'))
    }
  }, [isAccountUsersError, t])

  React.useEffect(() => {
    if (accountData?.account) {
      setEditedFields({
        display_name: accountData.account.display_name,
        contact_email: accountData.account.contact_email ?? '',
        is_active: accountData.account.is_active,
      })
    }
  }, [accountData])

  if (isLoading || isError || !accountData || !accountUID) {
    const message = isLoading ? t('LOADING') : t('ACCOUNT_NOT_FOUND_MESSAGE')
    return (
      <Layout>
        <Header>
          <Banner title={t('ACCOUNT_DETAILS')} />
        </Header>
        <MainContent>
          <Section>
            <Container size='sm'>
              <Text>{message}</Text>
            </Container>
          </Section>
        </MainContent>
      </Layout>
    )
  }

  const { account, application_accounts } = accountData
  const accountUsers = accountUsersData ?? []

  const handleNameUpdated = (e: EditableChangedEventArgs) => {
    const conditionedValue = EntityConditioner.Account({
      DisplayName: e.currentValue,
    })
    const trimmedName = conditionedValue.DisplayName || ''

    if (trimmedName === '') {
      setEditedFields(prev => ({ ...prev, display_name: account.display_name }))
      return
    }

    if (trimmedName !== editedFields.display_name) {
      setEditedFields(prev => ({
        ...prev,
        display_name: trimmedName,
      }))

      if (accountUID) {
        updateAccount({
          id: accountUID,
          data: { display_name: trimmedName },
        })
          .unwrap()
          .then(() => {
            toast.success(t('ACCOUNT_UPDATED_SUCCESSFULLY'))
          })
          .catch(() => {
            toast.error(t('ERROR_UPDATING_ACCOUNT'))
            setEditedFields(prev => ({
              ...prev,
              display_name: account.display_name,
            }))
          })
      }
    }
  }

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!accountUID) return

    // Validate email format
    if (
      editedFields.contact_email &&
      !isValidEmail(editedFields.contact_email)
    ) {
      toast.error(t('CONTACT_EMAIL_INVALID'))
      return
    }

    // Check if any fields have actually changed
    const hasChanges =
      editedFields.display_name !== account.display_name ||
      editedFields.contact_email !== (account.contact_email ?? '') ||
      editedFields.is_active !== account.is_active

    if (!hasChanges) {
      return
    }

    updateAccount({
      id: accountUID,
      data: {
        display_name: editedFields.display_name,
        contact_email: editedFields.contact_email,
        is_active: editedFields.is_active,
      },
    })
      .unwrap()
      .then(() => {
        toast.success(t('ACCOUNT_UPDATED_SUCCESSFULLY'))
      })
      .catch(() => {
        toast.error(t('ERROR_UPDATING_ACCOUNT'))
      })
  }

  const accountEditFormId = 'account-edit-form'

  return (
    <Layout>
      <Header>
        <Banner
          title={
            <Editable
              label={t('ACCOUNT_NAME')}
              maxLength={512}
              onUpdate={handleNameUpdated}
              required={true}
              value={editedFields.display_name}
              dataTestingId='account-name-input'
            />
          }
        />
        <Level>
          <LevelGroup position='right'>
            <LevelItem>
              <Buttons>
                {/* The staff route back to an import run on the account's behalf, or one
                    the customer needs help with — the public history page, which the
                    backend lets staff read for any account. */}
                <Button
                  label={t('VIEW_BULK_IMPORTS')}
                  theme='border'
                  onClick={() => navigate(`/account/${accountUID}/imports`)}
                  dataTestingId='account-details-bulk-imports'
                />
                <Button
                  label={t('CREATE_USER')}
                  onClick={() =>
                    navigate(`/user/create?accountId=${accountUID}`)
                  }
                />
              </Buttons>
            </LevelItem>
          </LevelGroup>
        </Level>
      </Header>

      <MainContent>
        <Section>
          <Container size='sm'>
            <Form id={accountEditFormId} onSubmit={handleSubmit}>
              <Groups>
                <Section padding={['none', 'none', 'lg', 'none']}>
                  <Group tag='div'>
                    <ControlGroup
                      label={t('ACCOUNT_SUBDOMAIN')}
                      layout='inline'
                      control={<Text>{account.keycloak_realm_id}</Text>}
                    />
                    <ControlGroup
                      label={t('CONTACT_EMAIL')}
                      control={
                        <Input
                          id='contact-email'
                          type='email'
                          value={editedFields.contact_email}
                          disabled={isSaving}
                          handleChange={(e: IControlChangeEventArgs) =>
                            setEditedFields(prev => ({
                              ...prev,
                              contact_email: e.currentValue,
                            }))
                          }
                        />
                      }
                      htmlFor='contact-email'
                    />
                    <ControlGroup
                      label={t('STATUS')}
                      control={
                        <Select
                          id='status'
                          selectedValue={editedFields.is_active ? 1 : 0}
                          selectOptions={[
                            { value: 1, displayText: t('ACTIVE') },
                            { value: 0, displayText: t('DISABLED') },
                          ]}
                          disabled={isSaving}
                          handleChange={(e: IControlChangeEventArgs) =>
                            setEditedFields(prev => ({
                              ...prev,
                              is_active: e.currentValue === 1,
                            }))
                          }
                        />
                      }
                      htmlFor='status'
                    />
                  </Group>
                </Section>

                {application_accounts.length > 0 && (
                  <Section
                    padding={[
                      'lg',
                      'none',
                      accountUsers.length > 0 ? 'lg' : 'none',
                      'none',
                    ]}
                  >
                    <Group tag='div'>
                      <Text textWeight='bold'>{t('ACCOUNT_APPLICATIONS')}</Text>
                      {application_accounts.map(appAccount => (
                        <ControlGroup
                          key={appAccount.application_account_id}
                          label={appAccount.application.name}
                          layout='inline'
                          control={
                            <Text>
                              {`${appAccount.status} (ID: ${appAccount.application_account_id})`}
                            </Text>
                          }
                        />
                      ))}
                    </Group>
                  </Section>
                )}

                {accountUsers.length > 0 && (
                  <Section padding={['lg', 'none', 'none', 'none']}>
                    <Group tag='div'>
                      <Text textWeight='bold'>
                        {t('MEMBERS')} ({accountUsers.length})
                      </Text>
                      {accountUsers.map(accountUser => (
                        <Level key={accountUser.id}>
                          <LevelGroup position='left'>
                            <LevelItem>
                              <Link
                                to={`/user/details/${accountUser.id}?accountId=${accountUID}`}
                              >
                                {accountUser.display_name}
                              </Link>
                            </LevelItem>
                          </LevelGroup>
                          <LevelGroup position='right'>
                            <LevelItem>
                              <Text size='sm' color='muted'>
                                {accountUser.email} - {accountUser.status}
                              </Text>
                            </LevelItem>
                          </LevelGroup>
                        </Level>
                      ))}
                    </Group>
                  </Section>
                )}
              </Groups>
            </Form>
          </Container>
        </Section>
      </MainContent>

      <Footer>
        <Section padding='lg'>
          <Level>
            <LevelGroup position='center'>
              <LevelItem>
                <Buttons>
                  <Button
                    disabled={isSaving}
                    label={t('CANCEL')}
                    theme='flat'
                    onClick={() => navigate(-1)}
                    dataTestingId='account-details-cancel'
                  />
                  <Form.Submit
                    formId={accountEditFormId}
                    label={isSaving ? t('SAVING') : t('SAVE')}
                    loading={isSaving}
                    dataTestingId='account-details-save'
                  />
                </Buttons>
              </LevelItem>
            </LevelGroup>
          </Level>
        </Section>
      </Footer>
    </Layout>
  )
}

export { AccountDetailsView }
